import { Fragment, useMemo, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Box,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { ColumnDef, Row, flexRender, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDown2, ArrowUp2 } from 'iconsax-reactjs';
import { FormattedMessage } from 'react-intl';

import CircularLoader from 'components/CircularLoader';
import { EmptyTable } from 'components/third-party/react-table';

type SortItem = {
  id: string;
  sort?: boolean;
  desc?: boolean;
};

type ColumnMeta = {
  className?: string;
  paddingLeft?: number | string;
  width?: number | string;
  verticalAlign?: 'top' | 'middle' | 'bottom';
  align?: 'left' | 'right' | 'center';
};

type NotFoundProps = {
  icon?: React.ComponentType<any> | null;
  text?: string;
  link?: string | null;
  disableButton?: boolean;
};

type GeneralTableProps<TData> = {
  icon?: React.ComponentType<any> | null;
  text?: string;
  link?: string | null;
  loading?: boolean;
  disableButton?: boolean;
  columns: ColumnDef<TData, any>[];
  data: TData[];
  striped?: boolean;
  sortArray?: string[];
  sort?: SortItem[];
  setSortingData?: (payload: { name: string }) => void;
  notFound?: boolean;
  search?: string;
  count?: number;
  size?: number;
  pageIndex?: number;
  handlePageChange?: (event: React.ChangeEvent<unknown>, value: number) => void;
  handleSizeChange?: (event: any) => void;
  selectedRowIndex?: number[];
  renderRowSubComponent?: (props: { row: Row<TData>; rowProps: { key: string } }) => React.ReactNode;
  collapseIds?: string[];
  getRowId?: (row: TData, index: number) => string;
  NotFoundComponent?: React.ComponentType<NotFoundProps>;
  /** Fires after default row selection handling (e.g. open detail modal). */
  onRowClick?: (row: TData) => void;
  hideHeader?: boolean;
  /** Scroll area max height for table body (default 430). */
  maxBodyHeight?: number | string;
  /** Stronger selected row affordance (e.g. inset primary border). */
  emphasizeSelectedRow?: boolean;
  /** Default `dense` matches existing tables; `comfortable` adds vertical padding for list-style rows. */
  cellPadding?: 'dense' | 'comfortable';
  getRowSx?: (row: TData) => SxProps<Theme> | undefined;
  showRowDividers?: boolean;
  tableContainerSx?: SxProps<Theme>;
};

const getStoredRow = () => {
  try {
    return localStorage.getItem('tableSelectedRow') || '';
  } catch {
    return '';
  }
};

const setStoredRow = (value: string) => {
  try {
    localStorage.setItem('tableSelectedRow', value);
  } catch {
    // ignore
  }
};

function getRowKey<TData>(row: Row<TData>, getRowId?: (row: TData, index: number) => string) {
  if (getRowId) return getRowId(row.original, row.index);
  return (row.original as any)?._id ?? row.id;
}

function getBodyCellStyle(meta: ColumnMeta | undefined, cellPadding: 'dense' | 'comfortable'): CSSProperties {
  const defaultLeft = cellPadding === 'comfortable' ? 24 : '16px';
  const paddingLeft = meta?.paddingLeft ?? defaultLeft;
  const leftVal = typeof paddingLeft === 'number' ? `${paddingLeft}px` : paddingLeft;

  if (cellPadding === 'comfortable') {
    return {
      paddingTop: 16,
      paddingBottom: 16,
      paddingRight: 24,
      paddingLeft: leftVal,
      verticalAlign: (meta?.verticalAlign ?? 'middle') as CSSProperties['verticalAlign'],
      textAlign: meta?.align,
      ...(meta?.width !== undefined ? { width: meta.width, maxWidth: meta.width } : {})
    };
  }

  return {
    padding: 3,
    paddingLeft: leftVal,
    verticalAlign: meta?.verticalAlign as CSSProperties['verticalAlign'] | undefined,
    textAlign: meta?.align,
    ...(meta?.width !== undefined ? { width: meta.width, maxWidth: meta.width } : {})
  };
}

export default function GeneralTable<TData>({
  icon,
  text,
  link,
  loading,
  disableButton,
  columns,
  data,
  striped,
  sortArray = [],
  sort = [],
  setSortingData,
  notFound,
  search,
  count,
  size,
  pageIndex,
  handlePageChange,
  handleSizeChange,
  selectedRowIndex,
  renderRowSubComponent,
  collapseIds,
  getRowId,
  NotFoundComponent,
  onRowClick,
  hideHeader,
  maxBodyHeight = 430,
  emphasizeSelectedRow,
  cellPadding = 'dense',
  getRowSx,
  showRowDividers,
  tableContainerSx
}: GeneralTableProps<TData>) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const [selectedRow, setSelectedRow] = useState(getStoredRow());

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualSorting: true
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  const showPagination = Boolean(size || count);

  const notFoundNode = useMemo(() => {
    if (!NotFoundComponent) return null;
    return <NotFoundComponent icon={icon ?? null} text={text} link={link} disableButton={disableButton} />;
  }, [NotFoundComponent, disableButton, icon, link, text]);

  return (
    <TableContainer sx={tableContainerSx}>
      <Box sx={{ maxHeight: maxBodyHeight, overflow: 'auto' }}>
        <Table stickyHeader size={cellPadding === 'comfortable' ? 'medium' : 'small'}>
          {!hideHeader ? (
          <TableHead sx={{ zIndex: renderRowSubComponent ? 3 : 2, position: 'relative' }}>
            {headerGroups.map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  if (header.isPlaceholder) {
                    return <TableCell key={header.id} />;
                  }

                  const columnId = header.column.id;
                  const sortData = sort ?? [];
                  const val = sortData.find((item) => item.id === columnId);

                  const headerValue = flexRender(header.column.columnDef.header, header.getContext());
                  const headerLabel = typeof headerValue === 'string' ? <FormattedMessage id={headerValue} /> : headerValue;

                  return (
                    <TableCell key={header.id} style={{ position: 'sticky' }}>
                      {columnId === 'checkbox' ? (
                        headerLabel
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 'max-content' }}>{headerLabel}</Box>
                          {sortArray.includes(columnId) && (
                            <Stack
                              sx={{ color: 'secondary.light', cursor: 'pointer' }}
                              onClick={() => setSortingData && setSortingData({ name: columnId })}
                            >
                              {/* {console.log(val, 'val')} */}
                              <ArrowUp2
                                size={16}
                                variant="Bold"
                                style={{
                                  color: val?.sort && !val?.desc ? theme.palette.secondary.main : 'inherit'
                                }}
                              />
                              <ArrowDown2
                                size={16}
                                variant="Bold"
                                style={{
                                  marginTop: -8,
                                  color: val?.desc ? theme.palette.secondary.main : 'inherit'
                                }}
                              />
                            </Stack>
                          )}
                        </Stack>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          ) : null}
          <TableBody {...(striped && { className: 'striped' })}>
            {rows.length > 0 ? (
              rows.map((row) => {
                const rowKey = getRowKey(row, getRowId);
                const rowSelectionKey = JSON.stringify({ id: rowKey, path: pathname });
                const isSelected = selectedRow === rowSelectionKey || (selectedRowIndex ? selectedRowIndex.includes(row.index) : false);

                return (
                  <Fragment key={row.id}>
                    <TableRow
                      sx={{
                        ...(onRowClick ? { cursor: 'pointer' } : {}),
                        ...(getRowSx?.(row.original) ?? {}),
                        ...(isSelected
                          ? {
                              backgroundColor:
                                theme.palette.mode === 'dark' ? '#3b698f !important' : '#cee6ff !important',
                              ...(emphasizeSelectedRow
                                ? { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}` }
                                : {})
                            }
                          : {}),
                        ...(showRowDividers && row.index < rows.length - 1
                          ? { borderBottom: `1px solid ${theme.palette.divider}` }
                          : {})
                      }}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const meta = cell.column.columnDef.meta as ColumnMeta | undefined;
                        return (
                          <TableCell
                            key={cell.id}
                            onClick={() => {
                              const value = JSON.stringify({ id: rowKey, path: pathname });
                              setStoredRow(value);
                              setSelectedRow(value);
                            }}
                            className={meta?.className}
                            style={getBodyCellStyle(meta, cellPadding)}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {collapseIds &&
                      collapseIds.length > 0 &&
                      (collapseIds as string[]).includes((row.original as any)?._id) &&
                      renderRowSubComponent &&
                      renderRowSubComponent({ row, rowProps: { key: row.id } })}
                  </Fragment>
                );
              })
            ) : !loading && notFound && (!search || search.length === 0) ? (
              notFoundNode ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    style={{
                      display: 'table-cell',
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      height: '100%',
                      fontWeight: 'bold'
                    }}
                  >
                    {notFoundNode}
                  </TableCell>
                </TableRow>
              ) : (
                <EmptyTable msg="No Data" colSpan={columns.length} />
              )
            ) : !loading && !notFound && search ? (
              <EmptyTable msg="No Data" colSpan={columns.length} />
            ) : !loading && rows.length === 0 ? (
              <EmptyTable msg="No Data" colSpan={columns.length} />
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  style={{
                    display: 'table-cell',
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    height: '20vh',
                    fontWeight: 'bold'
                  }}
                >
                  <CircularLoader />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {showPagination ? <Divider /> : null}
      {showPagination ? (
        <Box sx={{ p: 2, py: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              {size ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="secondary">
                      Row per page
                    </Typography>
                    <FormControl sx={{ m: 1 }}>
                      <Select
                        id="table-rows-per-page"
                        value={size}
                        onChange={handleSizeChange}
                        size="small"
                        defaultValue={10}
                        sx={{ '& .MuiSelect-select': { py: 0.75, px: 1.25 } }}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              ) : null}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
              {!count || count === 0 ? null : (
                <Pagination
                  onChange={handlePageChange}
                  count={Math.ceil(count / (size || 10))}
                  page={pageIndex}
                  variant="combined"
                  color="primary"
                />
              )}
            </Grid>
          </Grid>
        </Box>
      ) : null}
    </TableContainer>
  );
}
